import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { AppActions } from '../../../store/app/app.actions';
import { DocumentViewerActions } from '../../../store/document-viewer/document-viewer.actions';
import { UserProfileActions } from '../../../store/user-profile/user-profile.actions';
import { ViewerCommand } from '../../document-viewer/models/document-viewer.models';
import { DocumentViewerService } from '../../document-viewer/services/document-viewer.service';
import { HelpComponentMode } from '../../help/models/help.model';
import { ActivityEvent } from '../../home/models/activity-event.model';
import { Permission } from '../../shared/models/api/permissions.enum';
import { PermissionService } from '../../shared/services/permission.service';
import { findTour, GuidedTourId, VIEWER_TOUR_ID } from '../constants/guided-tours';
import { GUIDED_TOUR_ENGINE } from '../models/guided-tour-engine';

@Injectable()
export class GuidedTourEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);
  private readonly documentViewerService = inject(DocumentViewerService);
  private readonly engine = inject(GUIDED_TOUR_ENGINE);

  /** `?guidedTourId=` is the launch contract — deep links in external docs and emails rely on it. */
  startFromUrl$: Observable<unknown> = createEffect(
    () =>
      combineLatest([
        this.permissionService.hasEveryPermission([Permission.READ_GUIDED_TOUR]),
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
      ]).pipe(
        filter(([hasPermission]) => !!hasPermission),
        map(() => this.route.snapshot.queryParamMap.get('guidedTourId')),
        distinctUntilChanged(),
        filter((guidedTourId) => !!guidedTourId),
        map((guidedTourId) => {
          // `helpMode` is load-bearing: a ToggleHelp without one resets the panel to the Help tab,
          // so the reopen on completion would land on the wrong tab.
          this.store.dispatch(AppActions.ToggleHelp({ open: false, helpMode: HelpComponentMode.GuidedTours }));
          this.store.dispatch(AppActions.StartGuidedTour({ guidedTourId }));
        }),
      ),
    { dispatch: false },
  );

  startGuidedTour$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.StartGuidedTour),
        map(({ guidedTourId }) => ({ tourId: guidedTourId, steps: findTour(guidedTourId)?.steps ?? [] })),
        map(({ tourId, steps }) => {
          this.store.dispatch(DocumentViewerActions.SendCommand({ message: { commandType: ViewerCommand.StopGuidedTour, value: null } }));

          // The viewer owns this one; the webapp has no steps to run for it. The stop stays
          // inside the branch: `engine.start()` sequences its own replacement, so a preceding
          // `stop()` would race the outgoing teardown.
          if (tourId === GuidedTourId.ViewerGuide) {
            this.engine.stop();
            this.store.dispatch(
              DocumentViewerActions.SendCommand({ message: { commandType: ViewerCommand.StartGuidedTour, value: VIEWER_TOUR_ID } }),
            );
            return;
          }

          if (!steps.length) {
            return;
          }

          this.engine.start(steps, {
            onComplete: () => this.onTourCompleted(tourId, { reopenPanel: !this.isDashboardTourAtHome(tourId) }),
            onExit: () => this.clearTourQueryParams(),
          });
        }),
      ),
    { dispatch: false },
  );

  /** The viewer owns `viewer-navigation`; its completion is stored under `viewer-guide`. */
  viewerTourCompleted$: Observable<unknown> = createEffect(
    () =>
      this.documentViewerService.listenOnDocumentEvents<[ViewerCommand, string]>([ViewerCommand.GuidedTourCompleted]).pipe(
        map(() => {
          this.onTourCompleted(GuidedTourId.ViewerGuide, { reopenPanel: true });
          // This tour never reaches the runner, so nothing else would clear its params.
          this.clearTourQueryParams();
        }),
      ),
    { dispatch: false },
  );

  private onTourCompleted(tourId: string, { reopenPanel }: { reopenPanel: boolean }): void {
    this.store.dispatch(UserProfileActions.ToggleCompletedTours({ tourId, completed: true }));
    this.store.dispatch(AppActions.AddActivity({ activity: ActivityEvent.GuidedTourComplete, meta: { tourId } }));

    if (reopenPanel) {
      this.store.dispatch(AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.GuidedTours }));
    }
  }

  /** Finishing the intro tour where it started shouldn't bury the dashboard behind the panel. */
  private isDashboardTourAtHome(tourId: string): boolean {
    return tourId === GuidedTourId.Dashboard && this.router.url.split(/[?#]/)[0].endsWith('/home');
  }

  /** `replaceUrl` keeps the spent tour out of history, so Back doesn't replay it. */
  private clearTourQueryParams(): void {
    this.router.navigate([], {
      queryParams: { ...this.route.snapshot.queryParams, guidedTourId: undefined, forcedHover: undefined },
      replaceUrl: true,
    });
  }
}
